import type { HitSpec } from '@/systems/Combat';

/**
 * Move data — the per-attack tuning the combo system reads.
 *
 * Typed module (not raw JSON) so refactors are checked and HitSpec is shared
 * with the combat system. Per-hero overrides layer on in MY-1441+.
 */
export interface MoveDef {
  id: string;
  spec: HitSpec;
  /** Which animation clip this move drives (light vs heavy). */
  attackState: 'attackL' | 'attackH';
  /** Horizontal reach of the hitbox from the attacker's centre (px). */
  reach: number;
  /** Frames the hitbox stays active. */
  activeFrames: number;
  /** Frames before the next attack may start. */
  recovery: number;
  /** Launchers pop the target (bigger knockback; vertical pop added later). */
  launcher?: boolean;
}

/** Ground light chain — escalating damage; index = chain position. */
export const LIGHT_CHAIN: readonly MoveDef[] = [
  {
    id: 'light1',
    spec: { damage: 6, knockback: 10, hitstun: 14 },
    attackState: 'attackL',
    reach: 46,
    activeFrames: 6,
    recovery: 10,
  },
  {
    id: 'light2',
    spec: { damage: 7, knockback: 10, hitstun: 14 },
    attackState: 'attackL',
    reach: 48,
    activeFrames: 6,
    recovery: 10,
  },
  {
    id: 'light3',
    spec: { damage: 10, knockback: 20, hitstun: 18 },
    attackState: 'attackL',
    reach: 52,
    activeFrames: 7,
    recovery: 14,
  },
];

/** Heavy following a light string — the finisher/launcher. */
export const HEAVY_FINISHER: MoveDef = {
  id: 'finisher',
  spec: { damage: 16, knockback: 36, hitstun: 28 },
  attackState: 'attackH',
  reach: 56,
  activeFrames: 8,
  recovery: 22,
  launcher: true,
};

/** Heavy thrown cold (no preceding light string). */
export const HEAVY_STANDALONE: MoveDef = {
  id: 'heavy',
  spec: { damage: 12, knockback: 24, hitstun: 20 },
  attackState: 'attackH',
  reach: 50,
  activeFrames: 8,
  recovery: 20,
};

/** Heavy released after holding past the charge threshold. */
export const HEAVY_CHARGED: MoveDef = {
  id: 'heavyCharged',
  spec: { damage: 20, knockback: 30, hitstun: 26 },
  attackState: 'attackH',
  reach: 52,
  activeFrames: 9,
  recovery: 26,
};

/** Hold HEAVY at least this many frames to release a charged heavy. */
export const HEAVY_CHARGE_FRAMES = 30;

/** Dash-run light attack — higher reach and knockback than a grounded light. */
export const DASH_ATTACK: MoveDef = {
  id: 'dashAttack',
  spec: { damage: 9, knockback: 28, hitstun: 16 },
  attackState: 'attackL',
  reach: 60,
  activeFrames: 7,
  recovery: 14,
};

/** Jump attack — pressed while airborne; shorter recovery, dedicated air arc. */
export const JUMP_ATTACK: MoveDef = {
  id: 'jumpAttack',
  spec: { damage: 8, knockback: 20, hitstun: 16 },
  attackState: 'attackL',
  reach: 52,
  activeFrames: 7,
  recovery: 8,
};

/** Back attack — LIGHT pressed while holding the direction opposite to facing. */
export const BACK_ATTACK: MoveDef = {
  id: 'backAttack',
  spec: { damage: 14, knockback: 30, hitstun: 22 },
  attackState: 'attackH',
  reach: 50,
  activeFrames: 8,
  recovery: 20,
};

// ── Ray "Tank" Miller command-special move defs (MY-2436) ──────────────────────

/**
 * Tank Buster — toward + SPECIAL.
 * Charging shoulder tackle that pops the target airborne (launcher: true).
 * Heavy animation clip; wide reach; sets up juggle follow-ups.
 */
export const RAY_TANK_BUSTER: MoveDef = {
  id: 'rayTankBuster',
  spec: { damage: 18, knockback: 22, hitstun: 24 },
  attackState: 'attackH',
  reach: 64,
  activeFrames: 9,
  recovery: 28,
  launcher: true,
};

// ── Errol "Dread" Clarke command-special move defs (MY-2438) ──────────────────

/**
 * Boundary Smash — toward + SPECIAL (ERROL, MY-2438).
 * Errol winds up and releases a cricket-ball / bat shockwave as a horizontal
 * projectile (handled by ProjectileSystem). The MoveDef here captures the
 * caster's animation recovery; the actual damage is carried in
 * ERROL_BOUNDARY_SMASH_PROJ_DEF (ProjectileDef, in ProjectileSystem.ts import).
 *
 * attackState: 'attackH' — reuses anim_errol_attackh while sprite art is pending.
 * No reach / activeFrames hitbox is spawned from here — the projectile owns damage.
 */
export const ERROL_BOUNDARY_SMASH: MoveDef = {
  id: 'errolBoundarySmash',
  spec: { damage: 14, knockback: 28, hitstun: 20 },
  attackState: 'attackH',
  reach: 0, // no direct melee hitbox — damage delivered by ProjectileSystem
  activeFrames: 0,
  recovery: 30,
};

/**
 * Crease Sweep — down + SPECIAL (ERROL, MY-2438).
 * 360° spinning-bat AoE hitting BOTH sides. Implemented as one wide centred
 * hitbox spanning facing + back; high recovery trade-off for crowd control.
 * The Player wiring spawns TWO Hitboxes (forward + backward) from this def.
 */
export const ERROL_CREASE_SWEEP: MoveDef = {
  id: 'errolCreaseSweep',
  spec: { damage: 16, knockback: 32, hitstun: 22 },
  attackState: 'attackH',
  reach: 62,
  activeFrames: 10,
  recovery: 36,
};

// ── Kris "Hammer" Vance command-special move defs (MY-2418) ───────────────────

/**
 * Hammer Throw — toward + SPECIAL (KRIS, MY-2418).
 * Kris hurls his war-hammer as a returning projectile (boomerang arc).
 * attackState: 'attackH' (wind-up animation); actual damage via ProjectileSystem.
 * reach: 0 — no direct melee hitbox; the projectile owns damage on outward + return.
 */
export const KRIS_HAMMER_THROW: MoveDef = {
  id: 'krisHammerThrow',
  spec: { damage: 16, knockback: 24, hitstun: 22 },
  attackState: 'attackH',
  reach: 0, // projectile delivers damage
  activeFrames: 0,
  recovery: 28,
};

/**
 * Ground Zero — down + SPECIAL (KRIS, MY-2418).
 * Overhead slam driving a low shockwave AoE into the ground.
 * Wide hitbox — good juggle ender and anti-crowd tool. Longer recovery.
 */
export const KRIS_GROUND_ZERO: MoveDef = {
  id: 'krisGroundZero',
  spec: { damage: 22, knockback: 30, hitstun: 24 },
  attackState: 'attackH',
  reach: 72,
  activeFrames: 11,
  recovery: 38,
  launcher: true,
};

// ── Priya "Jaguar" Hassan command-special move defs (MY-2418) ─────────────────

/**
 * Tiger Knee — toward + SPECIAL (PRIYA, MY-2418).
 * Rising knee strike that pops the opponent airborne (launcher: true).
 * High reach, moderate recovery — Priya's primary juggle opener.
 */
export const PRIYA_TIGER_KNEE: MoveDef = {
  id: 'priyaTigerKnee',
  spec: { damage: 17, knockback: 26, hitstun: 26 },
  attackState: 'attackH',
  reach: 58,
  activeFrames: 8,
  recovery: 26,
  launcher: true,
};

/**
 * Jaguar Dive — air + SPECIAL (PRIYA, MY-2418).
 * Downward dive-kick executed only while airborne. Fast active window,
 * short recovery — converts air position into a ground combo.
 */
export const PRIYA_JAGUAR_DIVE: MoveDef = {
  id: 'priyaJaguarDive',
  spec: { damage: 14, knockback: 20, hitstun: 18 },
  attackState: 'attackH',
  reach: 50,
  activeFrames: 7,
  recovery: 18,
};

/**
 * Rising Fury — down + SPECIAL (PRIYA, MY-2418).
 * Flash-kick anti-air. Inverted-arc kick that pops airborne enemies and
 * stuffs jumping opponents; launcher for juggle follow-up from the ground.
 */
export const PRIYA_RISING_FURY: MoveDef = {
  id: 'priyaRisingFury',
  spec: { damage: 19, knockback: 28, hitstun: 28 },
  attackState: 'attackH',
  reach: 54,
  activeFrames: 9,
  recovery: 32,
  launcher: true,
};

// ── Ghost "Blade" Chen command-special move defs (MY-2418) ────────────────────

/**
 * Blade Rekka — toward + SPECIAL (GHOST, MY-2418).
 * Advancing multi-hit knife flurry — 3 rapid strikes with extended active
 * frames, each hit delivering moderate damage. Ghost closes distance quickly.
 */
export const GHOST_BLADE_REKKA: MoveDef = {
  id: 'ghostBladeRekka',
  spec: { damage: 8, knockback: 12, hitstun: 14 },
  attackState: 'attackL',
  reach: 48,
  activeFrames: 14, // multi-hit window — three overlapping slashes
  recovery: 22,
};

/**
 * Shadow Step — back + SPECIAL (GHOST, MY-2418).
 * Short backward reposition with brief i-frames. Low damage (it's an evade),
 * short recovery so Ghost can immediately counter. No launcher.
 */
export const GHOST_SHADOW_STEP: MoveDef = {
  id: 'ghostShadowStep',
  spec: { damage: 0, knockback: 0, hitstun: 0 },
  attackState: 'attackL',
  reach: 0, // pure reposition — no hitbox
  activeFrames: 0,
  recovery: 14,
};

/**
 * Flashbang — down + SPECIAL (GHOST, MY-2418).
 * Thrown stun grenade as a projectile. Delivers heavy hitstun (30 frames)
 * on contact, enabling a full combo punish. Low damage by design.
 * reach: 0 — damage and stun are carried by the projectile def.
 */
export const GHOST_FLASHBANG: MoveDef = {
  id: 'ghostFlashbang',
  spec: { damage: 8, knockback: 10, hitstun: 30 },
  attackState: 'attackL',
  reach: 0, // projectile delivers stun
  activeFrames: 0,
  recovery: 22,
};

// ── THE PRAETOR command-special move defs (100% unlock) ──────────────────────

export const PRAETOR_SHIELD_BASH: MoveDef = {
  id: 'praetorShieldBash',
  spec: { damage: 20, knockback: 36, hitstun: 26 },
  attackState: 'attackH',
  reach: 68,
  activeFrames: 9,
  recovery: 30,
  launcher: true,
};

export const PRAETOR_BATON_SHOCK: MoveDef = {
  id: 'praetorBatonShock',
  spec: { damage: 14, knockback: 18, hitstun: 46 },
  attackState: 'attackL',
  reach: 54,
  activeFrames: 8,
  recovery: 28,
};

// ── THE RT HON. HALLOWAY command-special move defs (14/14 joke unlock) ───────

export const HALLOWAY_BRIBE_TOSS: MoveDef = {
  id: 'hallowayBribeToss',
  spec: { damage: 1, knockback: 4, hitstun: 52 },
  attackState: 'attackH',
  reach: 0,
  activeFrames: 0,
  recovery: 34,
};

export const HALLOWAY_COWER_DODGE: MoveDef = {
  id: 'hallowayCowerDodge',
  spec: { damage: 0, knockback: 0, hitstun: 0 },
  attackState: 'attackH',
  reach: 0,
  activeFrames: 0,
  recovery: 20,
};
